import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, of, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, switchMap, catchError, map } from "rxjs/operators";
import { AzureSuggestsResult } from "../../azure-search/models/azure-suggests-result.model";
import { AzureSuggestsRequest } from "../../azure-search/models/azure-suggests-request.model";
import { AzureSearchService } from "../../azure-search/azure-search";
import { SearchRequest, SearchResultsService } from "../../search-results/search-results";
import { Location, LogService, MarketTrackingEvents } from "../../core/core";
import { LocalStorageService } from "../../core/services/local-storage.service";
import { GeolocationService } from "../../core/services/geolocation.service";
import { HeaderService } from "../header/header.service";
import { commerceSearchIntent, searchDestination } from "../../search-results/models/commerce-search-intent";

@Component({
  selector: "app-global-search-bar",
  templateUrl: "./global-search-bar.component.html",
  styleUrls: ["./global-search-bar.component.scss"],
})
export class GlobalSearchBarComponent implements OnInit, OnDestroy {
  searchRequest: SearchRequest;
  showSearchBar: boolean = true;
  mobileHeading: string = "";
  mobileBreadcrumbs: { label: string; url?: string }[] = [];
  private sub: Subscription;
  private searchBarSub: Subscription;
  private headingSub: Subscription;
  private breadcrumbSub: Subscription;

  private suggestsRequest = new AzureSuggestsRequest();

  constructor(
    private router: Router,
    private searchResultsService: SearchResultsService,
    private logService: LogService,
    private geolocationService: GeolocationService,
    private headerService: HeaderService,
    private azureSearchService: AzureSearchService,
    private localStorageService: LocalStorageService,
  ) { }

  ngOnInit() {
    this.searchRequest = this.searchResultsService.searchRequest;
    this.restoreStoredLocation();

    this.sub = this.searchResultsService.searchRequest$.subscribe(searchRequest => {
      this.searchRequest = searchRequest == null ? new SearchRequest() : searchRequest;
      this.restoreStoredLocation();
    });

    this.searchBarSub = this.headerService.showGlobalSearchBar.subscribe(show => {
      this.showSearchBar = show;
    });

    this.headingSub = this.headerService.mobileSearchHeading.subscribe(heading => {
      this.mobileHeading = heading;
    });

    this.breadcrumbSub = this.headerService.mobileSearchBreadcrumbs.subscribe(crumbs => {
      this.mobileBreadcrumbs = crumbs;
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.searchBarSub?.unsubscribe();
    this.headingSub?.unsubscribe();
    this.breadcrumbSub?.unsubscribe();
  }

  private restoreStoredLocation() {
    if (this.searchRequest?.location?.location?.latitude) {
      return;
    }
    const stored = this.localStorageService.get("current-location");
    if (stored?.latitude && stored?.longitude) {
      this.searchRequest.location.location = stored as Location;
    }
  }

  searchTypeahead = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        if (!term || term.length < 2) {
          return of([]);
        }
        this.suggestsRequest.search = term;
        if (term.length > 1 && term.endsWith("s")) {
          this.suggestsRequest.search = term.slice(0, -1);
        }
        this.suggestsRequest.fuzzy = false;
        return this.azureSearchService.suggestions(this.suggestsRequest, "suggestions").pipe(
          catchError(() => of([]))
        );
      })
    );

  searchResultFormatter = (result: AzureSuggestsResult) => result?.value || "";

  onSelectSearchSuggestion(event: any) {
    event.preventDefault();
    const suggestion = event.item as AzureSuggestsResult;
    this.searchRequest.searchTerm = suggestion.value;
    this.searchRequest.selectedSuggestion = suggestion;
    this.onSearch();
  }

  locationSelected(event: Location) {
    this.searchRequest.location.location = event;
    this.searchResultsService.storeLocation(event);
    if (event) {
      this.onSearch();
    }
  }

  useMyLocation() {
    this.geolocationService.getCurrentLocation().subscribe(location => {
      this.searchRequest.location.location = location;
      this.onSearch();
    });
  }

  onSearch() {
    const suggestion = this.searchRequest.selectedSuggestion;
    const intent = suggestion?.searchTarget
      ? { target: suggestion.searchTarget, query: suggestion.searchQuery ?? "" }
      : commerceSearchIntent(this.searchRequest.searchTerm || "");
    if (intent?.target === "attachments" || intent?.target === "parts") {
      const destination = searchDestination(intent.target, intent.query);
      this.router.navigate([destination.path], { queryParams: destination.queryParams });
      return;
    }
    let queryParams = this.searchRequest.queryParams;
    queryParams["q"] = this.searchRequest.searchTerm;
    this.router.navigate([this.searchRequest.searchResultsUrl()], {
      queryParams,
    });

    this.logService.trackEvent(
      MarketTrackingEvents.Search.Header,
      {
        event: "rentalSearch",
        searchTerm: this.searchRequest.searchTerm,
        location: this.searchRequest.location.toCityState(),
      },
      true,
    );
  }
}
